import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Add,
} from '@mui/icons-material';
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks } from 'date-fns';
import { AppointmentCalendarProps } from '../../types/appointment';

/** Normalize time to HH:mm for consistent comparison */
function normalizeTime(time: string): string {
  const match = time.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return time;
  const [, h, m] = match;
  return `${h!.padStart(2, '0')}:${m!.padStart(2, '0')}`;
}

/** Check if appointment start time falls within a 30-min slot (slotStart <= start < slotEnd) */
function isTimeInSlot(appointmentStart: string, slotTime: string): boolean {
  const apt = normalizeTime(appointmentStart);
  const slot = normalizeTime(slotTime);
  const slotIdx = timeSlots.indexOf(slot);
  if (slotIdx < 0) return false;
  const nextSlot = timeSlots[slotIdx + 1];
  if (!nextSlot) return apt >= slot; // last slot
  return apt >= slot && apt < nextSlot;
}

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00'
];

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  appointments,
  onAppointmentClick,
  onAddAppointment,
  onDateChange,
}) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday start
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const handlePreviousWeek = () => {
    const newWeek = subWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
    onDateChange?.(newWeek);
  };

  const handleNextWeek = () => {
    const newWeek = addWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
    onDateChange?.(newWeek);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentWeek(today);
    onDateChange?.(today);
  };

  const getAppointmentsForDateTime = (date: Date, time: string) => {
    return appointments.filter(apt => {
      let aptDate: Date;
      let aptTime: string;

      if ('appointmentDate' in apt && typeof (apt as any).appointmentDate === 'string') {
        const dateStr = (apt as any).appointmentDate;
        const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
        aptDate = new Date(y!, m! - 1, d!);
        aptTime = apt.startTime;
      } else {
        aptDate = new Date(apt.startTime);
        aptTime = format(aptDate, 'HH:mm');
      }

      return isSameDay(aptDate, date) && isTimeInSlot(aptTime, time);
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const isToday = (date: Date) => isSameDay(date, new Date());

  return (
    <div className="appointment-calendar">
    <Card sx={{ width: '100%', boxShadow: 'none', border: 'none' }}>
      <CardContent sx={{ p: { xs: 0, sm: 0 }, '&:last-child': { pb: 0 } }}>
        {/* Calendar Header */}
        <Box className="calendar-header" sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, sm: 2 },
          px: { xs: 0, sm: 0 }
        }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, fontWeight: 600 }}>
            Week of {format(weekStart, 'MMM dd, yyyy')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Today />}
              onClick={handleToday}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Today
            </Button>
            <IconButton onClick={handlePreviousWeek} size="small">
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={handleNextWeek} size="small">
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

        {/* Calendar Grid */}
        <Box className="calendar-scroll-container" sx={{ 
          overflowX: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}>
          <Grid container sx={{ minWidth: { xs: 650, sm: 850, md: 1050 } }}>
            {/* Time Column */}
            <Grid item xs={1.5}>
              <Box className="day-header time-column" sx={{ 
                height: { xs: 48, sm: 56 }, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderBottom: 1, 
                borderColor: 'divider',
                px: { xs: 0.5, sm: 1 },
                bgcolor: '#f5f5f5'
              }}>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  Time
                </Typography>
              </Box>
              {timeSlots.map((time) => (
                <Box
                  key={time}
                  className="calendar-cell time-column"
                  sx={{
                    height: { xs: 48, sm: 56 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: { xs: 0.5, sm: 1 },
                    bgcolor: '#fafafa'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    {time}
                  </Typography>
                </Box>
              ))}
            </Grid>

            {/* Day Columns */}
            {weekDays.map((day) => (
              <Grid item xs={1.5} key={day.toISOString()}>
                {/* Day Header */}
                <Box
                  className="day-header"
                  sx={{
                    height: { xs: 48, sm: 56 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    bgcolor: isToday(day) ? 'primary.light' : '#f0f0f0',
                    color: isToday(day) ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    {format(day, 'EEE')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, fontWeight: 600 }}>
                    {format(day, 'dd')}
                  </Typography>
                </Box>

                {/* Time Slots */}
                {timeSlots.map((time) => {
                  const dayAppointments = getAppointmentsForDateTime(day, time);
                  
                  return (
                    <Box
                      key={`${day.toISOString()}-${time}`}
                      className="calendar-cell"
                      sx={{
                        height: { xs: 48, sm: 56 },
                        borderBottom: 1,
                        borderLeft: 1,
                        borderColor: 'divider',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => onAddAppointment(day, time)}
                    >
                      {dayAppointments.map((appointment) => (
                        <Tooltip
                          key={appointment.id}
                          title={`${appointment.customerName} - ${appointment.serviceName} (${appointment.staffName})`}
                        >
                          <Chip
                            className="appointment-chip"
                            label={appointment.customerName}
                            size="small"
                            color={getStatusColor(appointment.status) as any}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAppointmentClick(appointment);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 2,
                              left: 2,
                              right: 2,
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: 'auto',
                              maxHeight: { xs: '40px', sm: '48px' },
                              '& .MuiChip-label': {
                                px: { xs: 0.5, sm: 1 },
                                py: { xs: 0.25, sm: 0.5 },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                      
                      {dayAppointments.length === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              opacity: 1,
                            },
                          }}
                        >
                          <Add fontSize="small" color="action" />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Legend */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0.75, sm: 1.5 }, 
          mt: { xs: 1.5, sm: 2 }, 
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          <Chip label="Scheduled" size="small" color="default" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
          <Chip label="Confirmed" size="small" color="success" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
          <Chip label="In Progress" size="small" color="warning" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
          <Chip label="Completed" size="small" color="info" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
          <Chip label="Cancelled" size="small" color="error" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
        </Box>
      </CardContent>
    </Card>
    </div>
  );
};

export default AppointmentCalendar;